import { useMemo } from "react"

import { useTransportMutation, useTransportQuery } from "@/transport"

import type { MetricOutputResource } from "@/types_api/backend/transport/get_metric_outputs_by_ulids_response"

import type { TestMetricResult } from "../types"

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function asRecordArray(value: unknown): Array<Record<string, unknown>> | null {
  if (!Array.isArray(value)) {
    return null
  }
  return value.flatMap((item) => {
    const record = asRecord(item)
    return record ? [record] : []
  })
}

export function normalizeMetricOutput(
  output: MetricOutputResource,
): TestMetricResult {
  return {
    ulid: output.ulid,
    simulation_output_id: output.simulation_output_id,
    output_type: output.output_type ?? "",
    float_value: output.float_value ?? null,
    string_value: output.string_value ?? null,
    set_value: output.set_value ?? null,
    result: asRecord(output.result),
    result_json: asRecord(output.result_json),
    evidence_references: output.evidence_references,
    subvalues_by_timestamp: asRecordArray(output.subvalues_by_timestamp),
    error_status: output.error_status ?? null,
    error_message: output.error_message ?? null,
    end_time: output.end_time ?? null,
  }
}

export function useRunTestMetricTransport() {
  return useTransportMutation("runTestMetric")
}

export function useMetricOutputsByUlidsTransport(
  metricOutputUlids: string[],
  options?: {
    enabled?: boolean
    refetchInterval?: number
  },
) {
  const request = useMemo(
    () => ({
      ulids:
        metricOutputUlids.length > 0
          ? (metricOutputUlids as [string, ...string[]])
          : ([""] as [string, ...string[]]),
    }),
    [metricOutputUlids],
  )

  return useTransportQuery<"getMetricOutputsByUlids", TestMetricResult[]>({
    operation: "getMetricOutputsByUlids",
    request,
    options: {
      enabled: Boolean(options?.enabled) && metricOutputUlids.length > 0,
      refetchInterval: options?.refetchInterval,
      select: (response) => response.metric_outputs.map(normalizeMetricOutput),
    },
  })
}
